import { MAINTENANCE_FETCH_TIMEOUT_MS, clampMessage } from './constants';
import { resolveProbeUrl } from './urls';

export interface RemoteFlag {
  maintenance: boolean;
  message: string;
}

/**
 * Fetch the maintenance flag from the Cloudflare Pages Function.
 * Returns null on any failure (network, non-OK status, malformed JSON).
 * Caller treats null as "no signal; continue probe sequence."
 *
 * Uses cache: 'default' so the browser HTTP cache honors the Pages
 * Function's Cache-Control: public, max-age=30, stale-while-revalidate=60.
 * With 'no-store' (the default for our other probes) every probe within the
 * 30s window would force-revalidate against the edge, defeating the cost
 * story for clients stuck in a maintenance window. Favicon and /health
 * probes intentionally keep 'no-store' because they need fresh signals.
 */
export async function fetchRemoteFlag(): Promise<RemoteFlag | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, MAINTENANCE_FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(resolveProbeUrl('/api/maintenance'), {
      method: 'GET',
      cache: 'default',
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const body: unknown = await res.json().catch(() => null);
    if (body === null || typeof body !== 'object') return null;
    const maintenance = (body as { maintenance?: unknown }).maintenance === true;
    const message = clampMessage((body as { message?: unknown }).message);
    return { maintenance, message };
  } catch (e) {
    // The contract is "null = no signal; continue probe sequence", so the
    // catch swallows by design. But operators have zero visibility into
    // why /api/maintenance is failing (CDN issue, CORS regression, JSON
    // parse error, etc.) without a breadcrumb. Fire-and-forget so the
    // catch is still total. Sentry SDK can throw; nested try/catch keeps
    // the contract intact.
    if (typeof window !== 'undefined') {
      try {
        window.Sentry?.addBreadcrumb?.({
          category: 'maintenance',
          message: 'remoteFlag fetch failed',
          level: 'warning',
          data: {
            reason:
              e instanceof DOMException && e.name === 'AbortError'
                ? 'aborted'
                : e instanceof TypeError
                  ? 'network'
                  : 'unknown',
          },
        });
      } catch {
        // Sentry SDK throwing must never break the probe path.
      }
    }
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}
